// Attribution tracking utility for capturing and persisting UTM parameters across the website session
// Also captures Meta Pixel cookies (_fbc, _fbp) for Conversions API

const ATTRIBUTION_STORAGE_KEY = 'helvetic_padel_attribution';

/**
 * Get a cookie value by name
 */
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

/**
 * Generate fbc from fbclid if _fbc cookie doesn't exist
 * Format: fb.1.{timestamp}.{fbclid}
 */
const generateFbcFromFbclid = (fbclid) => {
  if (!fbclid) return null;
  const timestamp = Date.now();
  return `fb.1.${timestamp}.${fbclid}`;
};

/**
 * Captures Meta Pixel cookies (_fbc, _fbp) for Conversions API
 * These are critical for event match quality
 */
export const captureMetaPixelCookies = () => {
  const fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');
  
  console.log('🍪 Meta Pixel cookies:', { fbc: fbc ? 'present' : 'missing', fbp: fbp ? 'present' : 'missing' });
  
  return { fbc, fbp };
};

/**
 * Captures attribution data from URL parameters and stores it in session storage
 * This should be called on the first page visit (typically in App.js)
 */
export const captureAttributionData = () => {
  // Check if we already have attribution data stored
  const existingAttribution = getStoredAttributionData();
  
  // Capture attribution data from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid') || null;
  
  // Capture Meta Pixel cookies
  const { fbc, fbp } = captureMetaPixelCookies();
  
  // If we have existing attribution, update it with fresh cookies
  if (existingAttribution) {
    // Always update fbc/fbp cookies as they may have been set after initial capture
    const updatedAttribution = {
      ...existingAttribution,
      fbc: fbc || existingAttribution.fbc || (fbclid ? generateFbcFromFbclid(fbclid) : null),
      fbp: fbp || existingAttribution.fbp
    };
    
    // Update storage if cookies changed
    if (updatedAttribution.fbc !== existingAttribution.fbc || updatedAttribution.fbp !== existingAttribution.fbp) {
      try {
        sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(updatedAttribution));
        console.log('🎯 Attribution data updated with Meta cookies:', { fbc: !!updatedAttribution.fbc, fbp: !!updatedAttribution.fbp });
      } catch (error) {
        console.error('❌ Error updating attribution data:', error);
      }
    }
    
    return updatedAttribution;
  }
  
  const attribution = {
    utm_source: urlParams.get('utm_source') || null,
    utm_medium: urlParams.get('utm_medium') || null,
    utm_campaign: urlParams.get('utm_campaign') || null,
    utm_content: urlParams.get('utm_content') || null,
    utm_term: urlParams.get('utm_term') || null,
    promo_code: urlParams.get('promo_code') || null, // Track promo code from URL
    fbclid: fbclid,
    fbc: fbc || (fbclid ? generateFbcFromFbclid(fbclid) : null), // Meta Click ID cookie
    fbp: fbp, // Meta Browser ID cookie
    gclid: urlParams.get('gclid') || null,
    ref: urlParams.get('ref') || null,
    source: urlParams.get('source') || null,
    referrer: document.referrer || '',
    timestamp: new Date().toISOString(),
    session_id: generateSessionId() // Add unique session ID for tracking
  };

  // Only store if we have meaningful attribution data
  const hasAttribution = attribution.utm_source || attribution.fbclid || attribution.fbc || attribution.fbp || attribution.gclid || attribution.ref || attribution.promo_code;
  
  if (hasAttribution) {
    try {
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
      console.log('🎯 Attribution data captured and stored:', {
        ...attribution,
        fbc: attribution.fbc ? 'present' : 'missing',
        fbp: attribution.fbp ? 'present' : 'missing'
      });
      
      // Clean up URL parameters for better UX (optional)
      cleanUrlParameters();
      
      return attribution;
    } catch (error) {
      console.error('❌ Error storing attribution data:', error);
      return attribution;
    }
  } else {
    console.log('🎯 No attribution data found in URL');
    return null;
  }
};

/**
 * Generates a unique session ID for tracking
 */
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

/**
 * Retrieves stored attribution data from session storage
 */
export const getStoredAttributionData = () => {
  try {
    const stored = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('❌ Error retrieving attribution data:', error);
    return null;
  }
};

/**
 * Cleans URL parameters from the browser address bar for better UX
 * This removes UTM parameters and other tracking parameters
 */
const cleanUrlParameters = () => {
  const url = new URL(window.location);
  const trackingParams = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'fbclid', 'gclid', 'ref', 'source'
  ];
  
  let hasTrackingParams = false;
  trackingParams.forEach(param => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      hasTrackingParams = true;
    }
  });
  
  if (hasTrackingParams) {
    // Replace the URL without the tracking parameters
    window.history.replaceState({}, document.title, url.pathname + url.search);
    console.log('🧹 Cleaned tracking parameters from URL');
  }
};

/**
 * Clears stored attribution data (useful for testing or reset)
 */
export const clearAttributionData = () => {
  try {
    sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    console.log('🗑️ Attribution data cleared');
  } catch (error) {
    console.error('❌ Error clearing attribution data:', error);
  }
};

/**
 * Gets attribution data for form submission
 * Returns stored attribution data with fresh Meta cookies
 */
export const getAttributionForSubmission = () => {
  let attribution = getStoredAttributionData();
  
  // Always get fresh Meta cookies at submission time
  const { fbc, fbp } = captureMetaPixelCookies();
  
  if (attribution) {
    // Update with fresh cookies
    attribution = {
      ...attribution,
      fbc: fbc || attribution.fbc,
      fbp: fbp || attribution.fbp
    };
    console.log('🎯 Using stored attribution data for submission:', {
      utm_source: attribution.utm_source,
      fbclid: attribution.fbclid ? 'present' : 'missing',
      fbc: attribution.fbc ? 'present' : 'missing',
      fbp: attribution.fbp ? 'present' : 'missing'
    });
    return attribution;
  }
  
  // Even if no stored attribution, return Meta cookies if available
  if (fbc || fbp) {
    console.log('🎯 No stored attribution, but Meta cookies found:', { fbc: !!fbc, fbp: !!fbp });
    return { fbc, fbp };
  }
  
  console.log('🎯 No attribution data available for submission');
  return null;
};

/**
 * Checks if attribution data exists (for debugging)
 */
export const hasAttributionData = () => {
  const attribution = getStoredAttributionData();
  console.log('🔍 Attribution data exists:', !!attribution);
  if (attribution) {
    console.log('🔍 Attribution details:', {
      source: attribution.utm_source,
      campaign: attribution.utm_campaign,
      session_id: attribution.session_id,
      timestamp: attribution.timestamp
    });
  }
  return !!attribution;
};